/* ═══════════════════════════════════════════════════════════════════════
 * RoomV2.js — Phase 4b of the FaceMeet redesign
 *
 * Drop-in replacement for RoomV2.js. Same default export, same route
 * (/v2/room/:roomId), same identity/auth/LiveKit flow.
 *
 * STRUCTURE
 *   1.  Imports + constants + helpers
 *   2.  Icon set (inline SVG, currentColor-driven)
 *   3.  Sub-components:
 *        • PreviewModal      — pre-join camera/mic check
 *        • VideoTile         — single participant tile
 *        • FocusedVideoView  — spotlight overlay (fresh track.attach)
 *        • FloatingReaction  — emoji float animation
 *        • EmojiPicker       — react bar
 *        • Toolbar           — bottom control rail (NEW: extracted)
 *        • MoreMenu          — mobile overflow sheet (NEW: extracted)
 *   4.  RoomPage (main) — all state + LiveKit handlers, much slimmer JSX
 *
 * PRESERVED VERBATIM
 *   - Every RoomEvent handler
 *   - Every data-channel message type (chat, kick-user, end-meeting,
 *     user-info, hand-raise, hand-lower-ack, message-deleted, reaction,
 *     ban-from-chat, unban-from-chat, group-chat-toggle, group-chat-whitelist,
 *     viewer-mic-grant, viewer-mic-revoke, role-change,
 *     lock-mute, unlock-mute, lock-camera, unlock-camera)
 *   - 5-second rejoin grace (host page-reload doesn't toast "left")
 *   - 5-second mic-grant-cleanup debounce (token swap doesn't drop perm)
 *   - 15-second meeting-status poller grace
 *   - Screen-share dual publish (video + audio track)
 *   - Cohost / viewer-mic reconnect with fresh token
 *   - Focus view fresh track.attach to avoid blackout
 *   - Self-share placeholder (no Droste recursion)
 *   - Preview-only-on-fresh-navigation (reload bypasses preview)
 *
 * CHANGED
 *   - Local toast stack → global toast.* (from facemeet-ui.js)
 *   - Inline JSX → small focused components
 *   - Path adjustments for the new primitive imports
 * ═══════════════════════════════════════════════════════════════════ */
import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import { Room, RoomEvent, ConnectionState, Track } from "livekit-client";

// Existing project modules — unchanged
import ChatBox from "../ChatBox_PartPanel/ChatBoxV2";
import ParticipantsPanel from "../ChatBox_PartPanel/ParticipantsPanelV2";
import { logger } from "../../../log/logger";

// Design system (Phases 1 + 2). Adjust the path if you placed the
// styles folder differently — these are the only two new imports.
import { Button, Spinner, toast, ThemeToggle} from "../../../styles/facemeet-ui";
import "./RoomV2.css"; // RoomV2.css from Phase 4a — class names unchanged

const MAX_CAMERAS = 6;
const REJOIN_GRACE_MS = 5000;
const EMOJI_LIST = ["❤️", "😂", "👏", "🔥", "😮", "😢", "💯", "🎉"];

/* ─────────────────────────────────────────────────────────────────────
   HELPERS
   ───────────────────────────────────────────────────────────────────── */
const isValidEmailFn = (v) =>
  typeof v === "string" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

const parseJoinError = (err) => {
  if (!err) return "Unable to join meeting";
  if (typeof err.detail === "string") return err.detail;
  if (Array.isArray(err.detail))
    return err.detail
      .map((e) => (typeof e === "string" ? e : e.msg || "Validation error"))
      .join("; ") || "Unable to join meeting";
  return "Unable to join meeting";
};

/* ═══════════════════════════════════════════════════════════════════════
   ICONS — line-art, 22px default, currentColor
═══════════════════════════════════════════════════════════════════════ */
const Svg = ({ children, size = 22, ...rest }) => (
  <svg
    width={size} height={size} viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true" {...rest}
  >
    {children}
  </svg>
);

const IconMic = ({ on = true, size }) => (
  <Svg size={size}>
    <path d="M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
    <path d="M19 10v1a7 7 0 0 1-14 0v-1" />
    <line x1="12" y1="18" x2="12" y2="22" />
    {!on && <line x1="3" y1="3" x2="21" y2="21" strokeWidth="2.4" />}
  </Svg>
);

const IconCam = ({ on = true, size }) => (
  <Svg size={size}>
    <path d="M3 7a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
    <path d="m16 10 5-3v10l-5-3" />
    {!on && <line x1="3" y1="3" x2="21" y2="21" strokeWidth="2.4" />}
  </Svg>
);

const IconChat = ({ size }) => (
  <Svg size={size}>
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </Svg>
);

const IconUsers = ({ size }) => (
  <Svg size={size}>
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </Svg>
);

const IconShare = ({ on = false, size }) => (
  <Svg size={size}>
    <rect x="2" y="4" width="20" height="14" rx="2" />
    <line x1="8" y1="22" x2="16" y2="22" />
    <line x1="12" y1="18" x2="12" y2="22" />
    {on && <circle cx="12" cy="11" r="3" fill="currentColor" stroke="none" />}
  </Svg>
);

const IconReact = ({ size }) => (
  <Svg size={size}>
    <path d="M12 21s-7-4.5-7-10a4 4 0 0 1 7-2.65A4 4 0 0 1 19 11c0 5.5-7 10-7 10z"
          fill="currentColor" stroke="none" />
  </Svg>
);

const IconHand = ({ size }) => (
  <Svg size={size}>
    <path d="M9 11V5a1.5 1.5 0 0 1 3 0v6" />
    <path d="M12 11V4a1.5 1.5 0 0 1 3 0v7" />
    <path d="M15 11V6a1.5 1.5 0 0 1 3 0v9a6 6 0 0 1-6 6h-1a6 6 0 0 1-5.2-3l-2.3-4a1.5 1.5 0 0 1 2.6-1.5l1.4 2.4" />
    <path d="M9 11V8a1.5 1.5 0 0 0-3 0v6" />
  </Svg>
);

const IconSpeaker = ({ on = true, size }) => (
  <Svg size={size}>
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    {on
      ? <><path d="M15.54 8.46a5 5 0 0 1 0 7.07" /><path d="M19.07 4.93a10 10 0 0 1 0 14.14" /></>
      : <><line x1="23" y1="9" x2="17" y2="15" /><line x1="17" y1="9" x2="23" y2="15" /></>
    }
  </Svg>
);

const IconMore = ({ size }) => (
  <Svg size={size}>
    <circle cx="5" cy="12" r="2" fill="currentColor" stroke="none" />
    <circle cx="12" cy="12" r="2" fill="currentColor" stroke="none" />
    <circle cx="19" cy="12" r="2" fill="currentColor" stroke="none" />
  </Svg>
);

const IconPhoneOff = ({ size }) => (
  <Svg size={size}>
    <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-3.71-3" />
    <path d="M5.05 5.05A19.79 19.79 0 0 1 4.18 2.18 2 2 0 0 1 6.18 0h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L10.16 7.91" />
    <line x1="2" y1="2" x2="22" y2="22" />
  </Svg>
);

/* ═══════════════════════════════════════════════════════════════════════
   PREVIEW MODAL — pre-join camera + mic check
   Logic identical to V2; only the JSX shell uses the new primitives.
═══════════════════════════════════════════════════════════════════════ */
function PreviewModal({ username, onJoin }) {
  const videoRef = useRef(null);
  const [stream, setStream] = useState(null);

  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [devices, setDevices] = useState({ audio: [], video: [] });
  const [selAudio, setSelAudio] = useState("");
  const [selVideo, setSelVideo] = useState("");
  const [micPerm, setMicPerm] = useState("pending");
  const [camPerm, setCamPerm] = useState("pending");
  const [permError, setPermError] = useState("");
  const [alwaysShow, setAlwaysShow] = useState(
    localStorage.getItem("fm_v3_preview") !== "false",
  );

useEffect(() => {
  let cancelled = false;
  let acquiredStream = null;

  (async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      if (cancelled) { s.getTracks().forEach(t => t.stop()); return; }
      // Mic muted in preview — we only need it for device-label unlock
      s.getAudioTracks().forEach(t => { t.enabled = false; });
      acquiredStream = s;
      setStream(s);
      setMicPerm("granted");
      setCamPerm("granted");
    } catch (err) {
      // Fallback: try each device separately (unchanged from your existing code)
      let videoStream = null;
      try {
        videoStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        if (cancelled) { videoStream.getTracks().forEach(t => t.stop()); return; }
        acquiredStream = videoStream;
        setStream(videoStream);
        setCamPerm("granted");
      } catch { setCamPerm("denied"); setCamOn(false); }
      try {
        const audioStream = await navigator.mediaDevices.getUserMedia({ video: false, audio: true });
        if (cancelled) { audioStream.getTracks().forEach(t => t.stop()); }
        else { audioStream.getAudioTracks().forEach(t => t.stop()); setMicPerm("granted"); }
      } catch { setMicPerm("denied"); setMicOn(false); }
      if (!videoStream && err.name === "NotAllowedError") {
        setPermError("Camera & microphone are blocked. Click the lock icon next to the URL to allow access.");
      }
    }

    // Enumerate devices (unchanged)
    try {
      const devList = await navigator.mediaDevices.enumerateDevices();
      if (cancelled) return;
      setDevices({
        audio: devList.filter(d => d.kind === "audioinput"),
        video: devList.filter(d => d.kind === "videoinput"),
      });
    } catch {}
  })();

  return () => {
    cancelled = true;
    if (acquiredStream) acquiredStream.getTracks().forEach(t => t.stop());
  };
}, [username]);

// EFFECT 2: attach stream to video + explicitly call play()
// Re-runs whenever stream changes (including null → real stream)
useEffect(() => {
  const video = videoRef.current;
  if (!video || !stream) return;
  if (video.srcObject !== stream) video.srcObject = stream;

  const attemptPlay = () => {
    video.play().catch(err => {
      // Autoplay blocked is rare here (we're muted) but log just in case
      console.warn("[preview] autoplay blocked:", err.message);
    });
  };

  if (video.readyState >= 2 /* HAVE_CURRENT_DATA */) {
    attemptPlay();
  } else {
    video.addEventListener("loadedmetadata", attemptPlay, { once: true });
    return () => video.removeEventListener("loadedmetadata", attemptPlay);
  }
}, [stream]);

  const toggleCam = () => {
    if (camPerm === "denied") {
      setPermError("Camera access is blocked. Allow it in your browser settings, then reload.");
      return;
    }
    const track = stream.current?.getVideoTracks()[0];
    if (track) track.enabled = !track.enabled;
    setCamOn((v) => !v);
  };

  const toggleMic = () => {
    if (micPerm === "denied") {
      setPermError("Microphone access is blocked. Allow it in your browser settings, then reload.");
      return;
    }
    setMicOn((v) => !v);
  };

const handleJoin = async () => {
  if (videoRef.current) videoRef.current.srcObject = null;
  if (stream) {
    stream.getTracks().forEach(t => { t.enabled = false; t.stop(); });
    setStream(null);
  }
  localStorage.setItem("fm_v3_preview", alwaysShow ? "true" : "false");
  await new Promise(r => setTimeout(r, 300));
  onJoin({
    micOn: micOn && micPerm !== "denied",
    camOn: camOn && camPerm !== "denied",
    audioDeviceId: selAudio,
    videoDeviceId: selVideo,
  });
};

  return (
    <div className="preview-overlay">
      <div className="preview-modal">
        <div className="preview-name">{username}</div>

        <div className="preview-video-wrap">
          <video
            ref={videoRef} autoPlay playsInline muted
            className={`preview-video ${camOn && camPerm === "granted" ? "" : "preview-video--off"}`}
          />
          {(!camOn || camPerm !== "granted") && (
            <div className="preview-avatar">
              {username?.charAt(0)?.toUpperCase() || "?"}
            </div>
          )}

          <div className="preview-controls-overlay">
            <button
              className={`preview-ctrl-btn ${micOn && micPerm === "granted" ? "on" : "off"}`}
              onClick={toggleMic}
              title={micPerm === "denied" ? "Microphone blocked" : micOn ? "Mute mic" : "Unmute mic"}
            >
              <IconMic on={micOn && micPerm === "granted"} size={20} />
              <span className="ctrl-label">
                {micPerm === "denied" ? "Mic Blocked" : micOn ? "Mic On" : "Mic Off"}
              </span>
            </button>
            <button
              className={`preview-ctrl-btn ${camOn && camPerm === "granted" ? "on" : "off"}`}
              onClick={toggleCam}
              title={camPerm === "denied" ? "Camera blocked" : camOn ? "Turn off camera" : "Turn on camera"}
            >
              <IconCam on={camOn && camPerm === "granted"} size={20} />
              <span className="ctrl-label">
                {camPerm === "denied" ? "Cam Blocked" : camOn ? "Cam On" : "Cam Off"}
              </span>
            </button>
          </div>
        </div>

        {permError && (
          <div className="preview-perm-error" role="alert">
            <span aria-hidden="true">⚠️</span>
            <span>{permError}</span>
          </div>
        )}

        <div className="preview-devices">
          <select
            value={selAudio} onChange={(e) => setSelAudio(e.target.value)}
            className="device-select" disabled={micPerm === "denied"}
          >
            <option value="">
              🎙 {devices.audio.length > 0 ? "Default Microphone" : "No microphone detected"}
            </option>
            {devices.audio.map((d, i) => (
              <option key={d.deviceId || i} value={d.deviceId}>
                {d.label || `Microphone ${i + 1}`}
              </option>
            ))}
          </select>
          <select
            value={selVideo} onChange={(e) => setSelVideo(e.target.value)}
            className="device-select" disabled={camPerm === "denied"}
          >
            <option value="">
              📷 {devices.video.length > 0 ? "Default Camera" : "No camera detected"}
            </option>
            {devices.video.map((d, i) => (
              <option key={d.deviceId || i} value={d.deviceId}>
                {d.label || `Camera ${i + 1}`}
              </option>
            ))}
          </select>
        </div>

        <div className="preview-footer">
          <label className="preview-checkbox">
            <input
              type="checkbox"
              checked={alwaysShow}
              onChange={(e) => setAlwaysShow(e.target.checked)}
            />
            Always show preview when joining
          </label>
          <button className="preview-join-btn" onClick={handleJoin}>
            Join Now
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   VIDEO TILE + FOCUS VIEW + FLOATING REACTION + EMOJI PICKER
═══════════════════════════════════════════════════════════════════════ */
function VideoTile({ trackObj, displayName, isMine, isScreenShare, raised, hidden, onClick }) {
  return (
    <div
      className={`video-tile ${isScreenShare ? "video-tile--screen" : ""} ${onClick ? "video-tile--clickable" : ""}`}
      onClick={onClick}
      title={onClick ? "Click to focus" : undefined}
      style={hidden ? { visibility: "hidden", pointerEvents: "none" } : undefined}
    >
      <div
        className="video-tile__media"
        style={{ transform: isMine && !isScreenShare ? "scaleX(-1)" : "none" }}
        ref={(el) => {
          if (!el || !trackObj?.element) return;
          if (el.firstChild === trackObj.element) return;
          el.innerHTML = "";
          el.appendChild(trackObj.element);
        }}
      />
      <div className="video-tile__name">
        {displayName}
        {isMine && " (You)"}
        {isScreenShare && " — Screen"}
        {raised && <span className="tile-hand">✋</span>}
      </div>
    </div>
  );
}

function FocusedVideoView({ trackObj, displayName, isMine, onClose }) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || !trackObj?.track) return;
    const el = trackObj.track.attach();
    el.autoplay = true; el.playsInline = true; el.muted = true;
    el.style.cssText = "width:100%;height:100%;object-fit:contain;display:block;";
    if (isMine && !trackObj.isScreenShare) el.style.transform = "scaleX(-1)";
    containerRef.current.appendChild(el);
    return () => {
      try { trackObj.track.detach(el); } catch { /* ignore */ }
      el.srcObject = null;
      el.remove();
    };
  }, [trackObj?.id, isMine]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="focus-view" onClick={onClose} title="Click to go back">
      <button className="focus-back-btn" onClick={(e) => { e.stopPropagation(); onClose(); }}>
        ← Back
      </button>
      <div className="focus-label">
        {displayName}
        {isMine && " (You)"}
        {trackObj?.isScreenShare && " — Screen Share"}
      </div>
      <div ref={containerRef} className="focus-media" onClick={(e) => e.stopPropagation()} />
    </div>
  );
}

function FloatingReaction({ emoji, senderName, side }) {
  return (
    <div className={`floating-reaction floating-reaction--${side}`} style={{ "--rand": Math.random() }}>
      <span className="floating-emoji">{emoji}</span>
      <span className="floating-sender">{senderName}</span>
    </div>
  );
}

function EmojiPicker({ onPick }) {
  return (
    <div className="emoji-picker" role="menu" aria-label="Reactions">
      {EMOJI_LIST.map((e) => (
        <button key={e} className="emoji-pick-btn" onClick={() => onPick(e)} role="menuitem">
          {e}
        </button>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   TOOLBAR — bottom control rail
   Pure presentation. All state lives in the parent.
═══════════════════════════════════════════════════════════════════════ */
function Toolbar({
  // State
  role, isPublisher, viewerMicGranted,
  isAudioEnabled, isVideoEnabled, isSpeakerEnabled,
  isScreenSharing, myHandRaised,
  showChat, showParticipants, showEmojiPicker, showMoreMenu,
  participantCount, unreadChat, roomId,
  // Handlers
  onToggleAudio, onToggleVideo, onToggleSpeaker, onToggleScreenShare,
  onToggleHandRaise, onToggleChat, onToggleParticipants,
  onToggleReactions, onToggleMoreMenu, onLeave,
}) {
  return (
    <div className="bottom-bar">
      {/* LEFT: meeting name (desktop only) */}
      <div className="bottom-bar__left">
        <span className="meeting-name">FaceMeet · {roomId.slice(0, 8)}…</span>
      </div>

      {/* CENTER: action buttons */}
      <div className="bottom-bar__center">
        {/* PRIMARY (always visible) */}

        {/* Audio */}
        <button
          className={`toolbar-btn ${isAudioEnabled ? "toolbar-btn--on" : "toolbar-btn--off"}`}
          onClick={onToggleAudio}
          title={isAudioEnabled ? "Mute microphone" : "Unmute microphone"}
          aria-pressed={isAudioEnabled}
          disabled={!isPublisher && !viewerMicGranted}
        >
          <span className="tb-icon"><IconMic on={isAudioEnabled} /></span>
          <span className="tb-label">{isAudioEnabled ? "Mute" : "Unmute"}</span>
        </button>

        {/* Video */}
        {isPublisher && (
          <button
            className={`toolbar-btn ${isVideoEnabled ? "toolbar-btn--on" : "toolbar-btn--off"}`}
            onClick={onToggleVideo}
            title={isVideoEnabled ? "Stop video" : "Start video"}
            aria-pressed={isVideoEnabled}
          >
            <span className="tb-icon"><IconCam on={isVideoEnabled} /></span>
            <span className="tb-label">{isVideoEnabled ? "Stop Video" : "Start Video"}</span>
          </button>
        )}

        {/* Chat */}
        <button
          className={`toolbar-btn ${showChat ? "toolbar-btn--active" : ""}`}
          onClick={onToggleChat}
          title="Chat"
        >
          <span className="tb-icon"><IconChat /></span>
          <span className="tb-label">Chat</span>
          {unreadChat > 0 && (
            <span className="tb-badge">{unreadChat > 99 ? "99+" : unreadChat}</span>
          )}
        </button>

        {/* React */}
        <button
          className={`toolbar-btn ${showEmojiPicker ? "toolbar-btn--active" : ""}`}
          onClick={onToggleReactions}
          title="Send a reaction"
          aria-haspopup="menu"
          aria-expanded={showEmojiPicker}
        >
          <span className="tb-icon"><IconReact /></span>
          <span className="tb-label">React</span>
        </button>

        {/* OVERFLOW (desktop visible / mobile collapsed into More menu) */}
        <div className="toolbar-overflow">
          <button
            className={`toolbar-btn ${showParticipants ? "toolbar-btn--active" : ""}`}
            onClick={onToggleParticipants}
            title="Participants"
          >
            <span className="tb-icon"><IconUsers /></span>
            <span className="tb-label">
              Participants <span className="tb-count">{participantCount}</span>
            </span>
          </button>

          {isPublisher && (
            <button
              className={`toolbar-btn ${isScreenSharing ? "toolbar-btn--active" : ""}`}
              onClick={onToggleScreenShare}
              title={isScreenSharing ? "Stop sharing" : "Share screen"}
              aria-pressed={isScreenSharing}
            >
              <span className="tb-icon"><IconShare on={isScreenSharing} /></span>
              <span className="tb-label">{isScreenSharing ? "Stop Share" : "Share"}</span>
            </button>
          )}

          <button
            className={`toolbar-btn ${myHandRaised ? "toolbar-btn--active" : ""}`}
            onClick={onToggleHandRaise}
            title={myHandRaised ? "Lower hand" : "Raise hand"}
            aria-pressed={myHandRaised}
          >
            <span className="tb-icon"><IconHand /></span>
            <span className="tb-label">{myHandRaised ? "Lower" : "Raise"}</span>
          </button>

          <button
            className={`toolbar-btn ${isSpeakerEnabled ? "toolbar-btn--on" : "toolbar-btn--off"}`}
            onClick={onToggleSpeaker}
            title={isSpeakerEnabled ? "Mute speakers" : "Unmute speakers"}
            aria-pressed={isSpeakerEnabled}
          >
            <span className="tb-icon"><IconSpeaker on={isSpeakerEnabled} /></span>
            <span className="tb-label">{isSpeakerEnabled ? "Speaker" : "Muted"}</span>
          </button>
        </div>

        {/* More — mobile only */}
        <button
          className={`toolbar-btn toolbar-btn--more ${showMoreMenu ? "toolbar-btn--active" : ""}`}
          onClick={onToggleMoreMenu}
          title="More options"
          aria-haspopup="menu"
          aria-expanded={showMoreMenu}
        >
          <span className="tb-icon"><IconMore /></span>
          <span className="tb-label">More</span>
        </button>
      </div>

      {/* RIGHT: End / Leave */}
      <div className="bottom-bar__right">
        <button
          className="toolbar-btn toolbar-btn--end"
          onClick={onLeave}
          title={role === "host" ? "End meeting" : "Leave meeting"}
        >
          <span className="tb-icon"><IconPhoneOff /></span>
          <span className="tb-label">{role === "host" ? "End" : "Leave"}</span>
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   MORE MENU — mobile bottom sheet for overflow actions
═══════════════════════════════════════════════════════════════════════ */
function MoreMenu({
  onClose,
  isPublisher, isScreenSharing, isSpeakerEnabled, myHandRaised,
  showParticipants, showChat,
  participantCount,
  onToggleParticipants, onToggleScreenShare,
  onToggleHandRaise, onToggleSpeaker,
}) {
  const wrap = (fn) => () => { fn(); onClose(); };

  return createPortal(
    <>
      <div className="more-menu-backdrop" onClick={onClose} />
      <div className="more-menu" role="menu" aria-label="More options">
        <button
          className={`more-menu__item ${showParticipants ? "more-menu__item--active" : ""}`}
          onClick={wrap(onToggleParticipants)}
          role="menuitem"
        >
          <IconUsers />
          <span>Participants</span>
          <span className="more-menu__count">{participantCount}</span>
        </button>

        {isPublisher && (
          <button
            className={`more-menu__item ${isScreenSharing ? "more-menu__item--active" : ""}`}
            onClick={wrap(onToggleScreenShare)}
            role="menuitem"
          >
            <IconShare on={isScreenSharing} />
            <span>{isScreenSharing ? "Stop Sharing" : "Share Screen"}</span>
          </button>
        )}

        <button
          className={`more-menu__item ${myHandRaised ? "more-menu__item--active" : ""}`}
          onClick={wrap(onToggleHandRaise)}
          role="menuitem"
        >
          <IconHand />
          <span>{myHandRaised ? "Lower Hand" : "Raise Hand"}</span>
        </button>

        <button
          className="more-menu__item"
          onClick={wrap(onToggleSpeaker)}
          role="menuitem"
        >
          <IconSpeaker on={isSpeakerEnabled} />
          <span>{isSpeakerEnabled ? "Mute Speakers" : "Unmute Speakers"}</span>
        </button>
      </div>
    </>,
    document.body,
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   ─────────────────────────────────────────────────────────────────────
   ║                         ROOM PAGE                                  ║
   ─────────────────────────────────────────────────────────────────────
   All state + LiveKit logic. Logic preserved from RoomV2.
═══════════════════════════════════════════════════════════════════════ */
function RoomPage() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Identity resolution
  const stateEmail = location.state?.email || null;
  const storedEmail = localStorage.getItem("userEmail");
  const email = stateEmail || (storedEmail && isValidEmailFn(storedEmail) ? storedEmail : null);
  const username = location.state?.username || localStorage.getItem("username") || "Guest";

  // Preview state (only on fresh navigation; reload skips)
  const isInitialJoin = !!(location.state?.username || location.state?.email);
  const shouldShowPreview = isInitialJoin && localStorage.getItem("fm_v3_preview") !== "false";
  const [showPreview, setShowPreview] = useState(shouldShowPreview);
  const [readyToConnect, setReadyToConnect] = useState(!shouldShowPreview);

  useEffect(() => {
    logger.info(
      `[room-mount] room=${roomId} isInitialJoin=${isInitialJoin} ` +
      `shouldShowPreview=${shouldShowPreview} hasLocationState=${!!location.state} ` +
      `prefetchedToken=${!!location.state?.prefetchedToken} ` +
      `prefetchedRole=${location.state?.prefetchedRole || "-"} ` +
      `email=${email || "(guest)"} username=${username}`,
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const previewSettingsRef = useRef({ micOn: true, camOn: true });

  // Core room state
  const [room, setRoom] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [isConnecting, setIsConnecting] = useState(true);
  const [role, setRole] = useState("viewer");
  const roleRef = useRef("viewer");
  const isReconnectingRef = useRef(false);
  const isPublisher = role === "host" || role === "coHost";

  // Media state
  const [isAudioEnabled, setIsAudioEnabled] = useState(false);
  const [isVideoEnabled, setIsVideoEnabled] = useState(false);
  const isForceMuted = useRef(false);
  const isCameraLocked = useRef(false);
  const audioEls = useRef([]);
  const [isSpeakerEnabled, setIsSpeakerEnabled] = useState(true);

  // Screen share
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const screenTrackRef = useRef(null);
  const screenAudioTrackRef = useRef(null);

  // Focus view
  const [focusedTrackId, setFocusedTrackId] = useState(null);

  // UI panels
  const [showParticipants, setShowParticipants] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);

  // "Are you sure you want to leave?" — shown when the user returns to
  // this tab after switching away / minimizing while in the meeting.
  // See the visibilitychange effect below for why this fires on RETURN
  // rather than at the moment of switching (browsers do not allow a
  // blocking custom dialog during the hide transition, especially on
  // mobile where the page is suspended immediately).
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const wasHiddenRef = useRef(false);

  // User / role maps
  const [userMap, setUserMap] = useState({});
  const userMapRef = useRef({});
  const participantRolesRef = useRef({});
  const [participantRoles, setParticipantRoles] = useState({});

  // Hand raise
  const [myHandRaised, setMyHandRaised] = useState(false);
  const [handRaisedMap, setHandRaisedMap] = useState({});
  const [hasRaisedHands, setHasRaisedHands] = useState(false);

  // Chat unread
  const [unreadGroup, setUnreadGroup] = useState(0);
  const [unreadPersonal, setUnreadPersonal] = useState(0);
  const [personalUnreadMap, setPersonalUnreadMap] = useState({});
  const chatOpenRef = useRef(false);
  const chatTypeRef = useRef("group");

  // Reactions
  const [reactions, setReactions] = useState([]);
  const reactionIdRef = useRef(0);

  // Ban-from-chat
  const [bannedFromChat, setBannedFromChat] = useState(new Set());

  // Group chat
  const [groupChatDisabled, setGroupChatDisabled] = useState(false);
  const [groupChatWhitelist, setGroupChatWhitelist] = useState(new Set());

  // Viewer mic
  const [viewerMicPermissions, setViewerMicPermissions] = useState(new Set());
  const [viewerMicGranted, setViewerMicGranted] = useState(false);

  // Misc
  const [meetingAgenda, setMeetingAgenda] = useState("");
  const fdaId = location.state?.fdaId || localStorage.getItem("fdaId") || "";
  const hasConnected = useRef(false);
  const roomRef = useRef(null);
  const addTrackRef = useRef(null);
  const joinedUsersRef = useRef(new Set());
  const recentDisconnectsRef = useRef(new Map());
  const recentMicGrantClearRef = useRef(new Map());

  const API_URL = process.env.REACT_APP_SERVER_URL;
  const LIVEKIT_URL = process.env.REACT_APP_LIVEKIT_URL;

  /* ── Track helpers ──────────────────────────────────────────────── */
  addTrackRef.current = (track, identity, sourceOverride) => {
    if (!track) return;
    const effectiveSource = sourceOverride || track.source;
    const isScreenShare =
      effectiveSource === Track.Source.ScreenShare ||
      effectiveSource === "screen_share";
    setTracks((prev) => {
      if (prev.some((t) => t.track === track)) return prev;
      if (!isScreenShare && prev.filter(t => !t.isScreenShare).length >= MAX_CAMERAS) {
        logger.warn(`Camera cap reached — dropping from ${identity}`);
        return prev;
      }
      const el = track.attach();
      el.autoPlay = true; el.playsInline = true; el.muted = true;
      el.style.cssText = "width:100%;height:100%;object-fit:cover;display:block;";
      logger.debug(
        `[track-add] identity=${identity} sid=${track.sid} ` +
        `effectiveSource=${effectiveSource} isScreenShare=${isScreenShare}`,
      );
      return [
        ...prev,
        { id: track.sid || `local-${Date.now()}`, track, identity, username: identity, element: el, isScreenShare },
      ];
    });
  };

  const removeTrack = (sid) => {
    setTracks((prev) => {
      prev.forEach((t) => {
        if ((t.id === sid || t.track?.sid === sid) && t.element) t.element.srcObject = null;
      });
      return prev.filter((t) => t.id !== sid && t.track?.sid !== sid);
    });
  };

  const removeParticipantTracks = (identity) => {
    setTracks((prev) => {
      prev.forEach((t) => { if (t.identity === identity && t.element) t.element.srcObject = null; });
      return prev.filter((t) => t.identity !== identity);
    });
  };

  const attachAudio = (track) => {
    const el = document.createElement("audio");
    el.autoplay = true; el.playsInline = true; el.muted = !isSpeakerEnabled;
    track.attach(el);
    document.body.appendChild(el);
    audioEls.current.push(el);
  };

  /* ── Media init ─────────────────────────────────────────────────── */
  const initMedia = async (lkRoom, overrides = {}) => {
    const { micOn = true, camOn = true } = { ...previewSettingsRef.current, ...overrides };
    logger.info(
      `[init-media] role=${roleRef.current} requested mic=${micOn} cam=${camOn} ` +
      `sdkCanPublish=${lkRoom.localParticipant.permissions?.canPublish}`,
    );
    try {
      await lkRoom.localParticipant.setCameraEnabled(camOn, {
        resolution: { width: 1280, height: 720 },
        videoPublishDefaults: {
          videoCodec: "h264",
          videoEncoding: { maxBitrate: 1_500_000, maxFramerate: 24 },
          scalabilityMode: "L1T3",
        },
        contentHint: "motion",
      });
      setIsVideoEnabled(camOn);
      await lkRoom.localParticipant.setMicrophoneEnabled(micOn);
      setIsAudioEnabled(micOn);
      logger.info(`[init-media] applied | cam=${camOn} mic=${micOn}`);
    } catch (err) {
      logger.error(`[init-media] FAILED | ${err.name}: ${err.message}`);
      toast.error(`Media error: ${err.message}`, { duration: 6000 });
    }
  };

  const initViewerAudio = async (lkRoom) => {
    try {
      await lkRoom.localParticipant.setMicrophoneEnabled(false);
      setIsAudioEnabled(false);
      logger.info("[init-viewer-audio] mic forced off");
    } catch (err) { logger.error(`[init-viewer-audio] FAILED | ${err.message}`); }
  };

  const stopAndUnpublishAllTracks = (lkRoom) => {
    const local = lkRoom.localParticipant;
    local.videoTrackPublications.forEach((pub) => {
      if (pub.track) { pub.track.stop(); local.unpublishTrack(pub.track); }
    });
    local.audioTrackPublications.forEach((pub) => {
      if (pub.track) { pub.track.stop(); local.unpublishTrack(pub.track); }
    });
    local.setCameraEnabled(false).catch(() => {});
    local.setMicrophoneEnabled(false).catch(() => {});
    setIsVideoEnabled(false); setIsAudioEnabled(false);
  };

  /* ── publishData (hardened with retry + state check) ────────────── */
  const publishData = async (payload, opts = {}) => {
    const { retries = 1, label = payload?.type || "data" } = opts;
    const r = roomRef.current;
    if (!r) { logger.warn(`[publishData:${label}] no room — dropped`); return false; }
    const tryOnce = async () => {
      if (r.state !== "connected") {
        logger.warn(`[publishData:${label}] room state=${r.state}, deferring`);
        return false;
      }
      try {
        await r.localParticipant.publishData(
          new TextEncoder().encode(JSON.stringify(payload)),
          { reliable: true },
        );
        logger.debug(`[publishData:${label}] ok | size=${JSON.stringify(payload).length}b`);
        return true;
      } catch (err) {
        logger.error(`[publishData:${label}] FAILED | ${err.name}: ${err.message}`);
        return false;
      }
    };
    let ok = await tryOnce();
    let attempts = 0;
    while (!ok && attempts < retries) {
      attempts++;
      await new Promise((res) => setTimeout(res, 250));
      ok = await tryOnce();
    }
    return ok;
  };

  /* ── Role helpers ───────────────────────────────────────────────── */
  const setParticipantRole = (identity, role) => {
    participantRolesRef.current = { ...participantRolesRef.current, [identity]: role };
    setParticipantRoles({ ...participantRolesRef.current });
  };
  const removeParticipantRole = (identity) => {
    const updated = { ...participantRolesRef.current };
    delete updated[identity];
    participantRolesRef.current = updated;
    setParticipantRoles(updated);
  };

  /* ── Reaction helpers ───────────────────────────────────────────── */
  const addFloatingReaction = useCallback((emoji, senderName) => {
    const id = ++reactionIdRef.current;
    const side = Math.random() > 0.5 ? "left" : "right";
    setReactions((prev) => [...prev, { id, emoji, senderName, side }]);
    // Auto-cleanup after animation completes
    setTimeout(() => {
      setReactions((prev) => prev.filter((r) => r.id !== id));
    }, 3500);
  }, []);

  const sendReaction = async (emoji) => {
    addFloatingReaction(emoji, "You");
    logger.info(`[reaction] sending | emoji=${emoji}`);
    const ok = await publishData(
      { type: "reaction", emoji, senderId: roomRef.current?.localParticipant?.identity, senderName: username },
      { label: "reaction", retries: 3 },
    );
    if (!ok) toast.error("Reaction couldn't be sent — try again", { duration: 2500 });
  };

  /* ── Ban helpers ────────────────────────────────────────────────── */
  const handleBanFromChat = (targetIdentity) => {
    setBannedFromChat((prev) => new Set([...prev, targetIdentity]));
    publishData({ type: "ban-from-chat", target: targetIdentity });
    toast.info(`${userMap[targetIdentity] || targetIdentity} muted from chat`);
  };

  const handleUnbanFromChat = (targetIdentity) => {
    setBannedFromChat((prev) => {
      const next = new Set(prev);
      next.delete(targetIdentity);
      return next;
    });
    publishData({ type: "unban-from-chat", target: targetIdentity });
    toast.info(`${userMap[targetIdentity] || targetIdentity} unmuted from chat`);
  };

  /* ── Mobile Back button for focused video ───────────────────────── */
  useEffect(() => { if (focusedTrackId) window.history.pushState({ focused: true }, ""); }, [focusedTrackId]);
  useEffect(() => {
    const h = () => { if (focusedTrackId) setFocusedTrackId(null); };
    window.addEventListener("popstate", h);
    return () => window.removeEventListener("popstate", h);
  }, [focusedTrackId]);

  /* ── Main connection effect ─────────────────────────────────────── */
  useEffect(() => {
    if (!readyToConnect) return;
    if (hasConnected.current) return;
    hasConnected.current = true;
    let activeRoom;

    const connectRoom = async () => {
      try {
        const guestIdentityStored = localStorage.getItem("guestIdentity");
        const guestIdentity = !email ? guestIdentityStored || null : null;

        const res = await fetch(`${API_URL}/v2/join-room`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, username, room: roomId, guestIdentity, fdaId }),
        });

        if (!res.ok) {
          const errBody = await res.json().catch(() => ({}));
          toast.error(parseJoinError(errBody));
          setTimeout(() => navigate("/v2"), 2500);
          return;
        }

        const data = await res.json();
        logger.info(`[join-room] response | identity=${data.identity} role=${data.role}`);
        if (fdaId) localStorage.setItem("fdaId", fdaId);

        if (data.identity) {
          if (isValidEmailFn(data.identity)) {
            localStorage.setItem("userEmail", data.identity);
            localStorage.removeItem("guestIdentity");
          } else {
            localStorage.setItem("guestIdentity", data.identity);
            localStorage.removeItem("userEmail");
          }
        }

        const currentRole = data.role || "viewer";
        roleRef.current = currentRole;
        setRole(currentRole);
        setParticipantRole(data.identity, currentRole);
        if (data.meetingAgenda) setMeetingAgenda(data.meetingAgenda);

        setUserMap((prev) => ({ ...prev, [data.identity]: data.username }));
        userMapRef.current = { [data.identity]: data.username };

        const newRoom = new Room({
          adaptiveStream: false, dynacast: false,
          reconnectPolicy: { nextRetryDelayInMs: () => null },
        });
        activeRoom = newRoom;
        roomRef.current = newRoom;
        setRoom(newRoom);

        await newRoom.connect(LIVEKIT_URL, data.token);
        logger.info(
          `[livekit-connect] connected | identity=${newRoom.localParticipant.identity} ` +
          `role=${currentRole} canPublish=${newRoom.localParticipant.permissions?.canPublish}`,
        );

        /* ─ Track events ─ */
        newRoom.on(RoomEvent.LocalTrackPublished, (pub) => {
          logger.info(`[local-track-published] kind=${pub.kind} src=${pub.source}`);
          if (pub.track?.kind === "video") {
            addTrackRef.current(pub.track, newRoom.localParticipant.identity, pub.source);
          }
        });
        newRoom.on(RoomEvent.LocalTrackUnpublished, (pub) => {
          if (pub.track) removeTrack(pub.track.sid);
        });
        newRoom.on(RoomEvent.TrackSubscribed, (track, pub, participant) => {
          if (track.kind === "video") addTrackRef.current(track, participant.identity, pub?.source);
          else if (track.kind === "audio") attachAudio(track);
        });
        newRoom.on(RoomEvent.TrackUnsubscribed, (track) => removeTrack(track.sid));

        /* ─ Permissions changed ─ */
        newRoom.on(RoomEvent.LocalParticipantPermissionsChanged, (prevPerms) => {
          const perms = newRoom.localParticipant.permissions;
          logger.info(`[perms-changed] prev.canPublish=${prevPerms?.canPublish} → now=${perms?.canPublish}`);
          if (perms?.canPublish && !prevPerms?.canPublish) {
            if (roleRef.current === "viewer") initViewerAudio(newRoom);
            else initMedia(newRoom);
          } else if (!perms?.canPublish && prevPerms?.canPublish) {
            logger.warn("[perms-changed] publish revoked — stopping all local tracks");
            stopAndUnpublishAllTracks(newRoom);
          }
        });

        /* ─ Participant list refresh ─ */
        const updateParticipants = () => {
          const list = [];
          newRoom.remoteParticipants.forEach((p) => {
            let isMuted = true, isVideoOff = true;
            p.audioTrackPublications.forEach((pub) => { if (pub.track && !pub.isMuted) isMuted = false; });
            p.videoTrackPublications.forEach((pub) => { if (pub.track && !pub.isMuted) isVideoOff = false; });
            list.push({ identity: p.identity, isMuted, isVideoOff, isLocal: false });
          });
          const local = newRoom.localParticipant;
          list.push({ identity: local.identity, isMuted: !local.isMicrophoneEnabled, isVideoOff: !local.isCameraEnabled, isLocal: true });
          setParticipants(list);
        };

        newRoom.on(RoomEvent.ParticipantConnected, (p) => {
          // Cancel pending "left" toast — host page-reload is invisible to others
          const pendingToast = recentDisconnectsRef.current.get(p.identity);
          if (pendingToast) { clearTimeout(pendingToast); recentDisconnectsRef.current.delete(p.identity); }
          // Cancel pending mic-permission cleanup (token-swap reconnect)
          const pendingMicClear = recentMicGrantClearRef.current.get(p.identity);
          if (pendingMicClear) {
            clearTimeout(pendingMicClear);
            recentMicGrantClearRef.current.delete(p.identity);
            logger.info(`[mic-grant-cleanup] cancelled — viewer reconnected | target=${p.identity}`);
          }
          newRoom.localParticipant.publishData(
            new TextEncoder().encode(JSON.stringify({
              type: "user-info",
              userId: newRoom.localParticipant.identity,
              username: data.username,
              role: roleRef.current,
            })),
            { reliable: true },
          );
          updateParticipants();
        });

        newRoom.on(RoomEvent.ParticipantDisconnected, (p) => {
          const pName = userMapRef.current[p.identity] || p.identity;
          const existing = recentDisconnectsRef.current.get(p.identity);
          if (existing) clearTimeout(existing);
          const tid = setTimeout(() => {
            recentDisconnectsRef.current.delete(p.identity);
            toast.info(`${pName} left the meeting`, { duration: 3000 });
          }, REJOIN_GRACE_MS);
          recentDisconnectsRef.current.set(p.identity, tid);

          removeParticipantTracks(p.identity);
          updateParticipants();
          removeParticipantRole(p.identity);

          // Defer mic-permission cleanup so token-swap reconnects don't drop the grant
          if (viewerMicPermissions.has(p.identity)) {
            const existingClear = recentMicGrantClearRef.current.get(p.identity);
            if (existingClear) clearTimeout(existingClear);
            const cleanupId = setTimeout(() => {
              recentMicGrantClearRef.current.delete(p.identity);
              setViewerMicPermissions((prev) => {
                if (!prev.has(p.identity)) return prev;
                const next = new Set(prev); next.delete(p.identity); return next;
              });
            }, REJOIN_GRACE_MS);
            recentMicGrantClearRef.current.set(p.identity, cleanupId);
          }
        });

        newRoom.on(RoomEvent.TrackMuted, updateParticipants);
        newRoom.on(RoomEvent.TrackUnmuted, updateParticipants);
        newRoom.on(RoomEvent.TrackPublished, updateParticipants);
        newRoom.on(RoomEvent.TrackUnpublished, updateParticipants);

        /* ─ Data messages — the central dispatcher ─ */
        newRoom.on(RoomEvent.DataReceived, async (payload) => {
          try {
            const msg = JSON.parse(new TextDecoder().decode(payload));
            const myIdentity = newRoom.localParticipant.identity;

            if (msg.type === "kick-user") {
              if (msg.target === myIdentity) {
                try { localStorage.removeItem("guestIdentity"); } catch (_) {}
                toast.error("You have been removed from the meeting");
                newRoom.disconnect();
                navigate("/v2");
              }
              return;
            }
            if (msg.type === "end-meeting") {
              toast.info("Meeting ended by host");
              newRoom.disconnect();
              navigate("/v2");
              return;
            }
            if (msg.type === "user-info") {
              setUserMap((prev) => ({ ...prev, [msg.userId]: msg.username }));
              if (!joinedUsersRef.current.has(msg.userId)) {
                joinedUsersRef.current.add(msg.userId);
                toast.info(`${msg.username} joined`, { duration: 3000 });
              }
              if (msg.role) setParticipantRole(msg.userId, msg.role);
              return;
            }
            if (msg.type === "chat") {
              window.dispatchEvent(new CustomEvent("chat-message", { detail: msg }));
              if (msg.senderId === myIdentity) return;
              const isChatClosed = !chatOpenRef.current;
              const isDifferentTab = chatOpenRef.current &&
                ((msg.chatType === "group" && chatTypeRef.current !== "group") ||
                 (msg.chatType === "personal" && chatTypeRef.current !== "personal"));
              if (msg.chatType === "group" && (isChatClosed || isDifferentTab))
                setUnreadGroup((c) => c + 1);
              if (msg.chatType === "personal" && msg.recipientId === myIdentity && (isChatClosed || isDifferentTab)) {
                setUnreadPersonal((c) => c + 1);
                setPersonalUnreadMap((prev) => ({ ...prev, [msg.senderId]: (prev[msg.senderId] || 0) + 1 }));
              }
              return;
            }
            if (msg.type === "hand-raise") {
              setHandRaisedMap((prev) => {
                const updated = { ...prev, [msg.senderId]: { raised: msg.raised, username: msg.senderName } };
                setHasRaisedHands(Object.values(updated).some((u) => u.raised));
                return updated;
              });
              return;
            }
            if (msg.type === "hand-lower-ack") {
              setHandRaisedMap((prev) => {
                const updated = { ...prev };
                delete updated[msg.target];
                setHasRaisedHands(Object.values(updated).some((u) => u.raised));
                return updated;
              });
              if (msg.target === myIdentity) setMyHandRaised(false);
              return;
            }
            if (msg.type === "message-deleted") {
              window.dispatchEvent(new CustomEvent("message-deleted", { detail: msg }));
              return;
            }
            if (msg.type === "reaction") {
              if (msg.senderId !== myIdentity)
                addFloatingReaction(msg.emoji, msg.senderName || userMapRef.current[msg.senderId] || "Someone");
              return;
            }
            if (msg.type === "ban-from-chat") {
              setBannedFromChat((prev) => new Set([...prev, msg.target]));
              return;
            }
            if (msg.type === "unban-from-chat") {
              setBannedFromChat((prev) => { const next = new Set(prev); next.delete(msg.target); return next; });
              return;
            }
            if (msg.type === "group-chat-toggle") { setGroupChatDisabled(msg.disabled); return; }
            if (msg.type === "group-chat-whitelist") { setGroupChatWhitelist(new Set(msg.whitelist || [])); return; }

            /* ── Viewer mic grant ── */
            if (msg.type === "viewer-mic-grant") {
              setViewerMicPermissions((prev) => new Set([...prev, msg.target]));
              if (msg.target !== myIdentity) return;
              logger.info(`[mic-grant-recv] target=${msg.target} tokenAttached=${!!msg.token}`);
              setViewerMicGranted(true);
              if (msg.token) {
                try {
                  isReconnectingRef.current = true;
                  await newRoom.disconnect();
                  await newRoom.connect(LIVEKIT_URL, msg.token);
                  isReconnectingRef.current = false;
                  newRoom.remoteParticipants.forEach((p) => {
                    p.trackPublications.forEach((pub) => {
                      if (pub.isSubscribed && pub.track) {
                        if (pub.track.kind === "video") addTrackRef.current(pub.track, p.identity, pub.source);
                        else if (pub.track.kind === "audio") attachAudio(pub.track);
                      }
                    });
                  });
                  newRoom.localParticipant.publishData(
                    new TextEncoder().encode(JSON.stringify({
                      type: "user-info",
                      userId: newRoom.localParticipant.identity,
                      username, role: roleRef.current,
                    })),
                    { reliable: true },
                  );
                } catch (err) {
                  isReconnectingRef.current = false;
                  logger.error("[mic-grant-recv] reconnect failed", err);
                  toast.error(`Mic activation failed: ${err.message}`);
                  return;
                }
              }
              toast.success("The host granted you mic access — tap the mic button to unmute");
              return;
            }
            if (msg.type === "viewer-mic-revoke") {
              setViewerMicPermissions((prev) => { const next = new Set(prev); next.delete(msg.target); return next; });
              if (msg.target !== myIdentity) return;
              setViewerMicGranted(false);
              setIsAudioEnabled(false);
              try { await newRoom.localParticipant.setMicrophoneEnabled(false); } catch (_) {}
              if (msg.token) {
                try {
                  isReconnectingRef.current = true;
                  await newRoom.disconnect();
                  await newRoom.connect(LIVEKIT_URL, msg.token);
                  isReconnectingRef.current = false;
                } catch (err) {
                  isReconnectingRef.current = false;
                  logger.error("[mic-revoke-recv] reconnect failed", err);
                }
              }
              toast.warning("Your microphone access has been revoked");
              return;
            }

            /* ── Role change (cohost promote / viewer demote) ── */
            if (msg.type === "role-change") {
              setParticipantRole(msg.target, msg.role);
              if (msg.target !== myIdentity) return;
              const newRole = msg.role;
              if (newRole === "coHost") {
                roleRef.current = "coHost"; setRole("coHost");
                toast.success("You are now a co-host — activating camera & mic…");
                if (!msg.token) { initMedia(newRoom); return; }
                try {
                  isReconnectingRef.current = true;
                  await newRoom.disconnect();
                  await newRoom.connect(LIVEKIT_URL, msg.token);
                  isReconnectingRef.current = false;
                  newRoom.localParticipant.publishData(
                    new TextEncoder().encode(JSON.stringify({
                      type: "user-info",
                      userId: newRoom.localParticipant.identity,
                      username, role: "coHost",
                    })),
                    { reliable: true },
                  );
                  newRoom.remoteParticipants.forEach((p) => {
                    p.trackPublications.forEach((pub) => {
                      if (pub.isSubscribed && pub.track) {
                        if (pub.track.kind === "video") addTrackRef.current(pub.track, p.identity, pub.source);
                        else if (pub.track.kind === "audio") attachAudio(pub.track);
                      }
                    });
                  });
                  await initMedia(newRoom);
                } catch (err) {
                  isReconnectingRef.current = false;
                  toast.error(`Co-host reconnect failed: ${err.message}`);
                }
              } else if (newRole === "viewer") {
                roleRef.current = "viewer"; setRole("viewer");
                toast.info("Your co-host role has been removed");
                stopAndUnpublishAllTracks(newRoom);
                if (msg.token) {
                  try {
                    isReconnectingRef.current = true;
                    await newRoom.disconnect();
                    await newRoom.connect(LIVEKIT_URL, msg.token);
                    isReconnectingRef.current = false;
                    newRoom.remoteParticipants.forEach((p) => {
                      p.trackPublications.forEach((pub) => {
                        if (pub.isSubscribed && pub.track) {
                          if (pub.track.kind === "video") addTrackRef.current(pub.track, p.identity, pub.source);
                          else if (pub.track.kind === "audio") attachAudio(pub.track);
                        }
                      });
                    });
                  } catch (err) { isReconnectingRef.current = false; }
                }
              }
              return;
            }

            /* ── Targeted lock/unlock for me ── */
            if (msg.target !== myIdentity) return;
            if (msg.type === "lock-mute") {
              isForceMuted.current = true;
              newRoom.localParticipant.setMicrophoneEnabled(false);
              setIsAudioEnabled(false);
              toast.warning("Your microphone was muted by the host");
            }
            if (msg.type === "unlock-mute") {
              isForceMuted.current = false;
              toast.info("You can now unmute your microphone");
            }
            if (msg.type === "lock-camera") {
              isCameraLocked.current = true;
              newRoom.localParticipant.setCameraEnabled(false);
              setIsVideoEnabled(false);
              toast.warning("Your camera was turned off by the host");
            }
            if (msg.type === "unlock-camera") {
              isCameraLocked.current = false;
              toast.info("Your camera has been re-enabled");
            }
          } catch (err) {
            logger.error("DataReceived parse error", err);
          }
        });

        /* ─ Connection state ─ */
        newRoom.on(RoomEvent.ConnectionStateChanged, async (state) => {
          if (isReconnectingRef.current) return;
          if (state === ConnectionState.Disconnected || state === ConnectionState.Failed) {
            toast.error("Connection lost — leaving meeting");
            setTimeout(() => navigate("/v2"), 1500);
          }
          if (state === ConnectionState.Reconnecting)
            toast.warning("Connection unstable — reconnecting…", { duration: 8000 });
        });

        newRoom.on(RoomEvent.Disconnected, (reason) => {
          if (isReconnectingRef.current) { setTracks([]); return; }
          const reasonStr = String(reason || "");
          const wasKicked = reasonStr === "PARTICIPANT_REMOVED" || reasonStr.includes("REMOVED");
          if (wasKicked) {
            try { localStorage.removeItem("guestIdentity"); } catch (_) {}
          }
          if (reasonStr === "ROOM_DELETED") toast.info("Meeting ended by host");
          else if (wasKicked) toast.error("You have been removed from the meeting");
          else toast.warning("You were disconnected");
          setTimeout(() => navigate("/v2"), 1500);
        });

        // Attach already-subscribed tracks
        newRoom.remoteParticipants.forEach((p) => {
          p.trackPublications.forEach((pub) => {
            if (pub.isSubscribed && pub.track) {
              if (pub.track.kind === "video") addTrackRef.current(pub.track, p.identity, pub.source);
              else if (pub.track.kind === "audio") attachAudio(pub.track);
            }
          });
        });

        updateParticipants();

        // Announce self
        newRoom.localParticipant.publishData(
          new TextEncoder().encode(JSON.stringify({
            type: "user-info",
            userId: newRoom.localParticipant.identity,
            username: data.username, role: currentRole,
          })),
          { reliable: true },
        );

        if (currentRole === "host" || currentRole === "coHost") await initMedia(newRoom);

        newRoom.localParticipant.videoTrackPublications.forEach((pub) => {
          if (pub.track) addTrackRef.current(pub.track, newRoom.localParticipant.identity, pub.source);
        });

        setIsConnecting(false);
      } catch (err) {
        logger.error("connectRoom error", { message: err.message });
        toast.error(`Connection failed: ${err.message}`);
        setTimeout(() => navigate("/v2"), 2500);
      }
    };

    connectRoom();

    return () => {
      if (activeRoom) {
        activeRoom.removeAllListeners();
        activeRoom.disconnect();
      }
      audioEls.current.forEach((el) => { el.srcObject = null; el.remove(); });
      audioEls.current = [];
      recentDisconnectsRef.current.forEach((tid) => clearTimeout(tid));
      recentDisconnectsRef.current.clear();
      recentMicGrantClearRef.current.forEach((tid) => clearTimeout(tid));
      recentMicGrantClearRef.current.clear();
    };
  }, [readyToConnect]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync userMap into tracks (display names update when joins arrive)
  useEffect(() => {
    setTracks((prev) => prev.map((t) => ({ ...t, username: userMap[t.identity] || t.identity })));
  }, [userMap]);

  useEffect(() => { userMapRef.current = userMap; }, [userMap]);

  // Audio unlock on first user gesture
  useEffect(() => {
    const unlock = () => { audioEls.current.forEach((el) => { el.muted = false; el.play().catch(() => {}); }); };
    document.addEventListener("click", unlock, { once: true });
    return () => document.removeEventListener("click", unlock);
  }, []);

  // Meeting status poller (with 15s startup grace)
  useEffect(() => {
    if (!room || isConnecting) return;
    const startTime = Date.now();
    const GRACE_MS = 15000;
    const iv = setInterval(async () => {
      if (Date.now() - startTime < GRACE_MS) return;
      try {
        const res = await fetch(`${API_URL}/v2/meeting-status/${roomId}`);
        const data = await res.json();
        if (data.status === 0) {
          if (data.reason === "duration_exceeded") {
            toast.info(
              "This meeting has reached its time limit for your plan and has ended.",
              { duration: 6000 },
            );
          } else {
            toast.info(data.reason === "host_disconnected"
              ? "Host disconnected — meeting ended"
              : "This meeting has ended");
          }
          const r = roomRef.current;
          if (r) r.disconnect();
          setTimeout(() => navigate("/v2"), 1500);
        }
      } catch (e) { logger.warn("Status poll failed", e); }
    }, 5000);
    return () => clearInterval(iv);
  }, [roomId, room, isConnecting]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Tab-switch / app-minimize "are you sure" confirmation ─────────
  // Two complementary mechanisms, because browsers do not give a page
  // any way to BLOCK a tab switch or app-minimize with a custom dialog:
  //
  // 1. visibilitychange: when the tab/app is hidden, we just note it
  //    (the page may be fully suspended on mobile, so nothing visual
  //    can happen here). When the user RETURNS (visibilitychange back
  //    to "visible") and we noted the tab had been hidden, we show our own
  //    Yes/No modal: "Are you sure you want to leave the meeting?"
  //    - Yes → leave the meeting (disconnect + navigate away)
  //    - No  → dismiss, stay connected
  //    This is the standard pattern used by conferencing apps for
  //    "are you still there?" prompts and is the closest a web page
  //    can get to gating tab-switches/minimizes on mobile.
  //
  // 2. beforeunload: covers the OTHER exit path — closing the tab,
  //    refreshing, or navigating to a different URL while connected.
  //    This one CAN be intercepted at the moment it happens, but only
  //    via the browser native "Leave site?" prompt — custom text
  //    and custom buttons are not permitted by any modern browser for
  //    security reasons. Setting `returnValue` is what triggers it.
  useEffect(() => {
    if (!room) return undefined;

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        wasHiddenRef.current = true;
        return;
      }
      // visibilityState === "visible"
      if (wasHiddenRef.current) {
        wasHiddenRef.current = false;
        // Avoid stacking on top of an already-open confirm (e.g. rapid
        // tab switching) or while another modal is already up.
        setShowLeaveConfirm(true);
      }
    };

    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [room]);


  /* ── Media controls ─────────────────────────────────────────────── */
  const toggleAudio = async () => {
    if (!room) return;
    const canUse = isPublisher || viewerMicGranted;
    if (!canUse) { toast.info("Ask the host to grant you mic access"); return; }
    if (isForceMuted.current) { toast.warning("Your microphone is locked by the host"); return; }
    const enable = !isAudioEnabled;
    try {
      await room.localParticipant.setMicrophoneEnabled(enable);
      if (enable) {
        const published = Array.from(room.localParticipant.audioTrackPublications.values())
          .some((pub) => pub.track && !pub.track.isMuted);
        if (!published) {
          toast.error("Mic didn't activate — please ask host to re-grant access", { duration: 5000 });
          setIsAudioEnabled(false);
          return;
        }
      }
      setIsAudioEnabled(enable);
    } catch (err) {
      logger.error(`[mic-toggle] ERROR | ${err.name}: ${err.message}`);
      toast.error(`Mic error: ${err.message}`);
    }
  };

  const toggleVideo = async () => {
    if (!room || !isPublisher) return;
    if (isCameraLocked.current) { toast.warning("Your camera is locked by the host"); return; }
    try {
      await room.localParticipant.setCameraEnabled(!isVideoEnabled);
      setIsVideoEnabled((v) => !v);
    } catch (err) {
      logger.error(`[cam-toggle] ERROR | ${err.message}`);
      toast.error(`Camera error: ${err.message}`);
    }
  };

  const toggleSpeaker = () => {
    const enable = !isSpeakerEnabled;
    audioEls.current.forEach((el) => { el.muted = !enable; });
    setIsSpeakerEnabled(enable);
  };

  const toggleHandRaise = () => {
    if (!room) return;
    const raised = !myHandRaised;
    setMyHandRaised(raised);
    publishData({ type: "hand-raise", senderId: room.localParticipant.identity, senderName: username, raised });
  };

  /* ── Screen share (dual-publish: video + tab audio) ─────────────── */
  const toggleScreenShare = async () => {
    if (!room || !isPublisher) return;
    try {
      if (!isScreenSharing) {
        const stream = await navigator.mediaDevices.getDisplayMedia({
          video: { width: { ideal: 1920 }, height: { ideal: 1080 }, frameRate: { ideal: 15 } },
          audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
        });
        const screenTrack = stream.getVideoTracks()[0];
        const screenAudioTrack = stream.getAudioTracks()[0] || null;
        screenTrack.contentHint = "detail";
        screenTrackRef.current = screenTrack;
        screenAudioTrackRef.current = screenAudioTrack;

        await room.localParticipant.publishTrack(screenTrack, {
          source: Track.Source.ScreenShare,
          videoEncoding: { maxBitrate: 3_000_000, maxFramerate: 15, priority: "high" },
          simulcast: false,
        });

        if (screenAudioTrack) {
          screenAudioTrack.contentHint = "music";
          try {
            await room.localParticipant.publishTrack(screenAudioTrack, {
              source: Track.Source.ScreenShareAudio,
              audioBitrate: 128_000, dtx: false, red: false,
            });
            logger.info("[screen-share] audio track published");
          } catch (audioPubErr) {
            logger.error(`[screen-share] audio publish FAILED | ${audioPubErr.message}`);
            toast.warning("Screen audio couldn't be shared — video will continue", { duration: 4000 });
          }
        } else {
          toast.info("Tip: to share sound, choose a Tab or Entire Screen and check 'Share audio'", { duration: 5000 });
        }

        setIsScreenSharing(true);

        screenTrack.onended = async () => {
          logger.info("[screen-share] browser ended sharing");
          try { await room.localParticipant.unpublishTrack(screenTrack); } catch (_) {}
          if (screenAudioTrackRef.current) {
            try { await room.localParticipant.unpublishTrack(screenAudioTrackRef.current); } catch (_) {}
            try { screenAudioTrackRef.current.stop(); } catch (_) {}
            screenAudioTrackRef.current = null;
          }
          setIsScreenSharing(false);
        };
        if (screenAudioTrack) {
          screenAudioTrack.onended = () => { screenAudioTrackRef.current = null; };
        }
      } else {
        const t = screenTrackRef.current;
        if (t) {
          try { await room.localParticipant.unpublishTrack(t); } catch (_) {}
          try { t.stop(); } catch (_) {}
          screenTrackRef.current = null;
        }
        const a = screenAudioTrackRef.current;
        if (a) {
          try { await room.localParticipant.unpublishTrack(a); } catch (_) {}
          try { a.stop(); } catch (_) {}
          screenAudioTrackRef.current = null;
        }
        setIsScreenSharing(false);
      }
    } catch (err) {
      if (err.name !== "NotAllowedError") {
        logger.error(`[screen-share] error | ${err.message}`);
      }
      setIsScreenSharing(false);
    }
  };

  /* ── Host actions ──────────────────────────────────────────────── */
  const handleLowerHand = (targetIdentity) => {
    publishData({ type: "hand-lower-ack", target: targetIdentity });
    setHandRaisedMap((prev) => {
      const updated = { ...prev };
      delete updated[targetIdentity];
      setHasRaisedHands(Object.values(updated).some((u) => u.raised));
      return updated;
    });
  };

  const endMeeting = async () => {
    localStorage.removeItem("fdaId");
    if (!room) return;
    publishData({ type: "end-meeting" });
    try {
      await fetch(`${API_URL}/v2/end-meeting`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ roomId, identity: room.localParticipant.identity }),
      });
    } catch (e) { logger.error("endMeeting failed", e); }
    await room.disconnect();
    navigate("/v2");
  };

  const leaveRoom = async () => {
    localStorage.removeItem("fdaId");
    if (role === "host") { await endMeeting(); return; }
    if (room) room.disconnect();
    navigate("/v2");
  };

  const handleAssignCoHost = async (targetEmail) => {
    if (!room) return;
    try {
      const res = await fetch(`${API_URL}/v2/assign-cohost`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ roomId, requesterId: room.localParticipant.identity, targetEmail }),
      });
      if (!res.ok) { const err = await res.json(); toast.error(err.detail || "Failed to assign co-host"); return; }
      const resData = await res.json();
      publishData({ type: "role-change", target: targetEmail, role: "coHost", token: resData.token });
      setParticipantRole(targetEmail, "coHost");
      toast.success(`${userMap[targetEmail] || targetEmail} is now a co-host`);
    } catch (e) { logger.error("handleAssignCoHost failed", e); }
  };

  const handleRemoveCoHost = async (targetEmail) => {
    if (!room) return;
    try {
      const res = await fetch(`${API_URL}/v2/remove-cohost`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ roomId, requesterId: room.localParticipant.identity, targetEmail }),
      });
      if (!res.ok) return;
      const resData = await res.json();
      publishData({ type: "role-change", target: targetEmail, role: "viewer", token: resData.token });
      setParticipantRole(targetEmail, "viewer");
      toast.info(`${userMap[targetEmail] || targetEmail} is no longer a co-host`);
    } catch (e) { logger.error("handleRemoveCoHost failed", e); }
  };

  const MAX_VIEWER_MICS = 2;

  const handleGrantViewerMic = async (targetIdentity) => {
    if (!room) return;
    if (viewerMicPermissions.size >= MAX_VIEWER_MICS) {
      toast.warning(`Maximum ${MAX_VIEWER_MICS} viewers with mic at once`);
      return;
    }
    try {
      setViewerMicPermissions((prev) => new Set([...prev, targetIdentity]));
      const res = await fetch(`${API_URL}/v2/grant-viewer-mic`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ roomId, requesterId: room.localParticipant.identity, targetIdentity }),
      });
      if (!res.ok) {
        setViewerMicPermissions((prev) => { const n = new Set(prev); n.delete(targetIdentity); return n; });
        toast.error("Failed to grant mic access");
        return;
      }
      const body = await res.json().catch(() => ({}));
      publishData({ type: "viewer-mic-grant", target: targetIdentity, token: body.token || null });
      toast.success(`Mic access granted to ${userMap[targetIdentity] || targetIdentity}`);
    } catch (e) {
      setViewerMicPermissions((prev) => { const n = new Set(prev); n.delete(targetIdentity); return n; });
      logger.error(`[mic-grant] EXCEPTION | target=${targetIdentity}`, e);
    }
  };

  const handleRevokeViewerMic = async (targetIdentity) => {
    if (!room) return;
    setViewerMicPermissions((prev) => { const n = new Set(prev); n.delete(targetIdentity); return n; });
    try {
      const res = await fetch(`${API_URL}/v2/revoke-viewer-mic`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ roomId, requesterId: room.localParticipant.identity, targetIdentity }),
      });
      const body = res.ok ? await res.json().catch(() => ({})) : {};
      publishData({ type: "viewer-mic-revoke", target: targetIdentity, token: body.token || null });
      toast.info(`Mic revoked for ${userMap[targetIdentity] || targetIdentity}`);
    } catch (e) { logger.error(`[mic-revoke] EXCEPTION`, e); }
  };

  const handleToggleGroupChat = (disabled) => {
    setGroupChatDisabled(disabled);
    publishData({ type: "group-chat-toggle", disabled });
    fetch(`${API_URL}/v2/toggle-group-chat`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        roomId, requesterId: room?.localParticipant?.identity,
        disabled, whitelist: [...groupChatWhitelist],
      }),
    }).catch((e) => logger.warn("toggle-group-chat API failed", e));
  };

  const handleWhitelistToggle = (identity) => {
    setGroupChatWhitelist((prev) => {
      const next = new Set(prev);
      if (next.has(identity)) next.delete(identity); else next.add(identity);
      const list = [...next];
      publishData({ type: "group-chat-whitelist", whitelist: list });
      fetch(`${API_URL}/v2/toggle-group-chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          roomId, requesterId: room?.localParticipant?.identity,
          disabled: groupChatDisabled, whitelist: list,
        }),
      }).catch((e) => logger.warn("whitelist API failed", e));
      return next;
    });
  };

  const copyRoomId = () => {
    navigator.clipboard.writeText(roomId);
    toast.success("Room ID copied");
  };

  const generateLink = () => {
    const link = `${window.location.origin}/v2/join/${roomId}`;
    navigator.clipboard.writeText(link)
      .then(() => toast.success("Invite link copied 🔗"))
      .catch(() => toast.info(`Link: ${link}`, { duration: 7000 }));
  };

  /* ── Derived layout values ─────────────────────────────────────── */
  const myIdentity = room?.localParticipant?.identity;

  const isScreenTrack = (t) =>
  t.isScreenShare ||
  t.track?.source === Track.Source.ScreenShare ||
  t.track?.source === "screen_share";
  const screenTracks = tracks.filter((t) => isScreenTrack(t));
  const cameraTracks = tracks.filter((t) => !isScreenTrack(t));

const remoteScreenTracks = screenTracks.filter(t => t.identity !== myIdentity);
const iAmSharing = screenTracks.some(t => t.identity === myIdentity);
const hasAnyScreenShare = screenTracks.length > 0;
const totalUnreadChat = unreadGroup + unreadPersonal;

  /* ── Toolbar handler bundle (stable refs not needed; render-cheap) ─ */
  const onToggleChat = () => {
    const next = !showChat;
    setShowChat(next); setShowParticipants(false);
    if (next) { chatOpenRef.current = true; setUnreadGroup(0); }
    else chatOpenRef.current = false;
  };
  const onToggleParticipants = () => { setShowParticipants((v) => !v); setShowChat(false); };
  const onToggleReactions = () => setShowEmojiPicker((v) => !v);
  const onToggleMoreMenu = () => setShowMoreMenu((v) => !v);

  /* ── Render ─────────────────────────────────────────────────────── */
  return (
    <div className="room-page">
      {/* PRE-JOIN PREVIEW */}
      {showPreview && (
        <PreviewModal
          username={username}
          onJoin={(settings) => {
            previewSettingsRef.current = { micOn: settings.micOn, camOn: settings.camOn };
            setShowPreview(false);
            setReadyToConnect(true);
          }}
        />
      )}

      {/* CONNECTING */}
      {isConnecting && !showPreview && (
        <div className="connecting-overlay">
          <Spinner size="lg" />
          <p>Joining meeting…</p>
        </div>
      )}

      {/* FLOATING REACTIONS */}
      {reactions.map((r) => (
        <FloatingReaction key={r.id} emoji={r.emoji} senderName={r.senderName} side={r.side} />
      ))}

      {/* EMOJI PICKER — portalled to escape toolbar stacking ctx */}
      {showEmojiPicker && createPortal(
        <>
          <div className="emoji-picker-backdrop" onClick={() => setShowEmojiPicker(false)} />
          <EmojiPicker onPick={(emoji) => { sendReaction(emoji); }} />
        </>,
        document.body,
      )}
{!meetingAgenda && (
  <div className="room-page__theme-fixed">
    <ThemeToggle />
  </div>
)}
      {/* MAIN ROOM */}
      {room && !showPreview && (
        <>
          {/* Agenda bar */}
          {meetingAgenda && (
            <div className="room-agenda-bar">
              <span className="agenda-label">Agenda</span>
              <span>{meetingAgenda}</span>
              <span style={{ flex: 1 }} />
              <span className={`role-chip role-chip--${role}`}>{role}</span>
              {hasRaisedHands && role === "host" && (
                <span className="hand-alert-chip">✋ Hand raised</span>
              )}
              {role === "host" && (
                <>
                  <button className="slim-action-btn" onClick={copyRoomId}>Copy ID</button>
                  <button className="slim-action-btn slim-action-btn--link" onClick={generateLink}>🔗 Invite</button>
                </>
              )}
                  <ThemeToggle />
            </div>
          )}

          {/* Focus view (fresh attach) */}
          {focusedTrackId && (() => {
            const ft = tracks.find((t) => t.id === focusedTrackId);
            return ft ? (
              <FocusedVideoView
                trackObj={ft}
                displayName={userMap[ft.identity] || ft.identity}
                isMine={ft.identity === myIdentity}
                onClose={() => setFocusedTrackId(null)}
              />
            ) : null;
          })()}

          {/* Screen share stage */}
{!focusedTrackId && hasAnyScreenShare && (
  <div className="screen-share-area">
    {remoteScreenTracks.map(t => (
      <VideoTile
        key={t.id}
        trackObj={t}
        displayName={userMap[t.identity] || t.identity}
        isMine={false}
        isScreenShare
        onClick={() => setFocusedTrackId(t.id)}
      />
    ))}

    {iAmSharing && remoteScreenTracks.length === 0 && (
      <div className="screen-share-self">
        <div className="screen-share-self__icon">🖥️</div>
        <div className="screen-share-self__title">You are sharing your screen</div>
        <div className="screen-share-self__hint">
          Other participants can see your shared content
        </div>
        <button className="screen-share-self__stop" onClick={toggleScreenShare}>
          Stop Sharing
        </button>
      </div>
    )}
  </div>
)}
          {/* Gallery */}
<div className={`camera-gallery ${hasAnyScreenShare ? "camera-gallery--strip" : "camera-gallery--full"}`}>
  <div
    className="gallery-scroll"
    data-count={Math.min(cameraTracks.length, 16)}
  >
    {cameraTracks.map((t) => (
      <VideoTile
        key={t.id}
        trackObj={t}
        displayName={userMap[t.identity] || t.identity}
        isMine={t.identity === myIdentity}
        isScreenShare={false}
        raised={handRaisedMap[t.identity]?.raised}
        hidden={focusedTrackId === t.id}
        onClick={() => setFocusedTrackId(focusedTrackId === t.id ? null : t.id)}
      />
    ))}
    {cameraTracks.length === 0 && !hasAnyScreenShare && (
      <div className="empty-gallery">
        <p>{isPublisher ? "Camera is off — click Start Video below" : "Waiting for the host's camera…"}</p>
      </div>
    )}
  </div>
</div>

          {/* Chat panel */}
          {showChat && (
            <div className="side-panel">
              <ChatBox
                room={room} roomId={roomId} username={username}
                userId={myIdentity || email || ""}
                participants={participants}
                userMap={userMap}
                personalUnreadMap={personalUnreadMap}
                setPersonalUnreadMap={setPersonalUnreadMap}
                role={role}
                participantRoles={participantRoles}
                unreadGroupExternal={unreadGroup}
                unreadPersonalExternal={unreadPersonal}
                groupChatDisabled={groupChatDisabled}
                groupChatAllowed={
                  role === "host" ||
                  !groupChatDisabled ||
                  groupChatWhitelist.has(myIdentity || email || "")
                }
                bannedFromChat={bannedFromChat}
                onChatOpen={(type) => {
                  chatOpenRef.current = true;
                  chatTypeRef.current = type;
                  if (type === "group") setUnreadGroup(0);
                  if (type === "personal") setUnreadPersonal(0);
                }}
                onChatClose={() => { chatOpenRef.current = false; setShowChat(false); }}
              />
            </div>
          )}

          {/* Participants panel (host + co-host) */}
          {(role === "host" || role === "coHost") && showParticipants &&
            createPortal(
              <ParticipantsPanel
                participants={participants}
                isHost={role === "host"} isCoHost={role === "coHost"}
                roomId={roomId} room={room} username={username}
                userMap={userMap}
                unreadPersonal={unreadPersonal}
                participantRoles={participantRoles}
                handRaisedMap={handRaisedMap}
                activeCameraCount={cameraTracks.length}
                maxCameras={MAX_CAMERAS}
                groupChatDisabled={groupChatDisabled}
                groupChatWhitelist={groupChatWhitelist}
                onToggleGroupChat={handleToggleGroupChat}
                onToggleWhitelist={handleWhitelistToggle}
                onClose={() => setShowParticipants(false)}
                onLowerHand={handleLowerHand}
                onAssignCoHost={handleAssignCoHost}
                onRemoveCoHost={handleRemoveCoHost}
                viewerMicPermissions={viewerMicPermissions}
                onGrantViewerMic={handleGrantViewerMic}
                onRevokeViewerMic={handleRevokeViewerMic}
                bannedFromChat={bannedFromChat}
                onBanFromChat={handleBanFromChat}
                onUnbanFromChat={handleUnbanFromChat}
              />,
              document.body,
            )}

          {/* Toolbar */}
          <Toolbar
            role={role}
            isPublisher={isPublisher}
            viewerMicGranted={viewerMicGranted}
            isAudioEnabled={isAudioEnabled}
            isVideoEnabled={isVideoEnabled}
            isSpeakerEnabled={isSpeakerEnabled}
            isScreenSharing={isScreenSharing}
            myHandRaised={myHandRaised}
            showChat={showChat}
            showParticipants={showParticipants}
            showEmojiPicker={showEmojiPicker}
            showMoreMenu={showMoreMenu}
            participantCount={participants.length}
            unreadChat={totalUnreadChat}
            roomId={roomId}
            onToggleAudio={toggleAudio}
            onToggleVideo={toggleVideo}
            onToggleSpeaker={toggleSpeaker}
            onToggleScreenShare={toggleScreenShare}
            onToggleHandRaise={toggleHandRaise}
            onToggleChat={onToggleChat}
            onToggleParticipants={onToggleParticipants}
            onToggleReactions={onToggleReactions}
            onToggleMoreMenu={onToggleMoreMenu}
            onLeave={leaveRoom}
          />

          {/* More menu (mobile) */}
          {showMoreMenu && (
            <MoreMenu
              onClose={() => setShowMoreMenu(false)}
              isPublisher={isPublisher}
              isScreenSharing={isScreenSharing}
              isSpeakerEnabled={isSpeakerEnabled}
              myHandRaised={myHandRaised}
              showParticipants={showParticipants}
              showChat={showChat}
              participantCount={participants.length}
              onToggleParticipants={onToggleParticipants}
              onToggleScreenShare={toggleScreenShare}
              onToggleHandRaise={toggleHandRaise}
              onToggleSpeaker={toggleSpeaker}
            />
          )}
        </>
      )}

      {showLeaveConfirm && createPortal(
        <div
          className="fm-leave-confirm"
          role="dialog"
          aria-modal="true"
          aria-label="Leave meeting confirmation"
        >
          <div
            className="fm-leave-confirm__backdrop"
            onClick={() => setShowLeaveConfirm(false)}
          />
          <div className="fm-leave-confirm__panel" onClick={(e) => e.stopPropagation()}>
            <h3 className="fm-leave-confirm__title">
              Are you sure you want to leave the meeting?
            </h3>
            <p className="fm-leave-confirm__body">
              You switched away from this tab. You are still connected to
              the meeting — tap "No" to keep your camera and mic active
              here, or "Yes" to leave.
            </p>
            <div className="fm-leave-confirm__actions">
              <button
                type="button"
                className="fm-leave-confirm__btn fm-leave-confirm__btn--ghost"
                onClick={() => setShowLeaveConfirm(false)}
                autoFocus
              >
                No, stay
              </button>
              <button
                type="button"
                className="fm-leave-confirm__btn fm-leave-confirm__btn--danger"
                onClick={() => {
                  setShowLeaveConfirm(false);
                  leaveRoom();
                }}
              >
                Yes, leave
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

export default RoomPage;